import { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Configure, InstantSearch, SearchBox } from 'react-instantsearch';
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { useSearchData } from '@/contexts/searchContext';
import { useAuthLinks } from '@/hooks/useAuthLinks';
import { useBreadcrumb } from '@/hooks/useBreadcrumb';
import { useComponentFactory } from '@/hooks/useComponentFactory';
import { useMenuToggle } from '@/hooks/useMenuToggle';
import { useMiniBasket } from '@/hooks/useMiniBasket';
import { useNewsletterSignup } from '@/hooks/useNewsletterSignup';
import { useProductPurchasing } from '@/hooks/useProductPurchasing';
import { SearchHeader } from '@/components/SearchHeader';
import { FilterPanel } from '@/components/FilterPanel';
import { ResultsPanel } from '@/components/ResultsPanel';
import { createRouting } from '@/utils/searchRouting';
import * as Styled from './SearchApp.styled';

export const SearchApp = () => {
  const { algolia } = useSearchData();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const authLinks = useAuthLinks();
  const menuToggle = useMenuToggle();
  const miniBasket = useMiniBasket();
  const breadcrumb = useBreadcrumb();
  const purchaseOptions = useProductPurchasing();
  const componentFactory = useComponentFactory();
  const newsletterSignup = useNewsletterSignup();

  const searchClient = useMemo(
    () => algoliasearch(algolia.applicationId, algolia.searchApiKey),
    [algolia.applicationId, algolia.searchApiKey],
  );

  const routing = useMemo(
    () => createRouting(algolia.productIndex),
    [algolia.productIndex],
  );

  const handleFilterToggle = useCallback(() => {
    setIsFilterOpen(prev => !prev);
  }, []);

  const handleFilterClose = useCallback(() => {
    setIsFilterOpen(false);
  }, []);

  const queryHook = useCallback(
    (query: string, refine: (value: string) => void) => {
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => refine(query), 300);
    },
    [],
  );

  // The header search slot is rendered by the Razor layout, outside this React
  // root. Portaling the SearchBox into it keeps the box inside the <InstantSearch>
  // context (React context follows the component tree, not the DOM) while its
  // markup renders in the header.
  const headerSearchEl = document.getElementById('headerSearchRoot');

  return (
    <>
      <InstantSearch
        indexName={algolia.productIndex}
        insights={true}
        routing={routing}
        searchClient={searchClient}
      >
        <Configure
          clickAnalytics={true}
          distinct={true}
          enablePersonalization={true}
        />

        {headerSearchEl &&
          createPortal(
            <Styled.SearchBoxWrapper>
              <SearchBox
                placeholder="Search for books, stationery, games, and gifts..."
                queryHook={queryHook}
                submitIconComponent={() => (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                )}
              />
            </Styled.SearchBoxWrapper>,
            headerSearchEl,
          )}

        <Styled.Container>
          <SearchHeader />

          <Styled.Layout>
            <FilterPanel isOpen={isFilterOpen} onClose={handleFilterClose} />
            <ResultsPanel onFilterToggle={handleFilterToggle} />
          </Styled.Layout>
        </Styled.Container>
      </InstantSearch>
      {authLinks}
      {menuToggle}
      {miniBasket}
      {breadcrumb}
      {purchaseOptions}
      {componentFactory}
      {newsletterSignup}
    </>
  );
};
